import { Fragment } from 'react';
import { PositionsGroup } from './PositionsGroup';
import { PositionsRow } from './PositionsRow';
import { Box } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { PositionsHOAIPhase } from './PositionsHOAIPhase';
import { PositionsPercent } from './PositionsPercent';

export const Position = ({
  props,
  index,
  item,
  items,
  activeItem,
  projectedIndex,
  dynamicColumns,
  horizontalDelta,
  posColWidth,
  groupKey,
  collapseModules,
  imageType,
  imageApiFilter,
  descriptionVars,
  showVars,
  openAmount,
  calculateDepthFromDelta,
  onFocus,
  setItems,
  calculateNewPositions,
  onEditHOAI,
  handleDelete,
  handleNewPosition,
}) => {
  const findPreviousAndNext = (items, activeItem, currentItem) => {
    const index = items.findIndex((el) => el.keyId == currentItem.keyId);
    const activeIndex = items.findIndex((el) => el.keyId == activeItem.keyId);

    let offset = 0;
    if (activeItem.isGroup) {
      const activeDepth = items[activeIndex].depth;
      let counter = activeIndex + 1;
      while (items[counter].depth > activeDepth) {
        counter++;
      }
      offset = counter - activeIndex;
    }

    let prevIndex;
    if (items[activeIndex].isGroup && activeIndex + offset == index) {
      prevIndex = index - offset - 1;
    } else if (index - 1 == activeIndex) {
      prevIndex = index - 2;
    } else {
      prevIndex = index - 1;
    }

    const nextIndex = index === activeIndex ? index - 1 : index;

    return [
      prevIndex < 0 ? { isGroup: false, depth: 0 } : items[prevIndex],
      nextIndex >= items.length
        ? { isGroup: false, depth: 0 }
        : items[nextIndex],
    ];
  };

  if (item.sortId === activeItem?.sortId) {
    return <tr className="h-0"></tr>;
  }

  if (item.hidden) {
    return <tr className="h-0"></tr>;
  }

  const shouldInsertPreview =
    projectedIndex === index || projectedIndex === items.length;

  return (
    <Fragment key={'' + item.keyId + item.id + 'PositionElement'}>
      {shouldInsertPreview && (
        <PreviewElement
          colSpan={dynamicColumns.length}
          depth={calculateDepthFromDelta(
            horizontalDelta,
            activeItem.depth,
            findPreviousAndNext(items, activeItem, items[index])
          )}
          posColWidth={posColWidth}
        />
      )}
      {item.isLastPlaceholder ? (
        item.sortId && (
          <DropDummy
            key={item.keyId}
            item={item}
            colSpan={dynamicColumns.length}
          />
        )
      ) : item.type == 'percent' ? (
        <PositionsPercent
          props={props}
          key={'drag-' + item.keyId + item.id + '-' + 'Row'}
          collapseModules={collapseModules}
          index={index}
          items={items}
          item={item}
          columns={dynamicColumns}
          imageType={imageType}
          imageApiFilter={imageApiFilter}
          descriptionVars={descriptionVars}
          showVars={showVars}
          openAmount={openAmount}
          onFocus={(ref, key) =>
            onFocus({
              index: index,
              key,
              ref: ref,
            })
          }
          onChange={(newItems) => {
            setItems(calculateNewPositions(newItems, false, true));
          }}
          onCreate={handleNewPosition}
          onDelete={handleDelete}
          isDragging={Boolean(activeItem)}
        />
      ) : item.isGroup ? (
        <PositionsGroup
          {...props}
          key={'drag-' + item.keyId + item.id + '-' + 'Group'}
          index={index}
          items={items}
          item={item}
          columns={dynamicColumns}
          groupKey={groupKey}
          collapseModules={collapseModules}
          onFocus={(ref, key) =>
            onFocus({
              index: index,
              key,
              ref: ref,
            })
          }
          onChange={(newItems) => {
            setItems(calculateNewPositions(newItems, false, true));
          }}
          onEdit={item.editableHOAI ? onEditHOAI : null}
          onDelete={handleDelete}
          isDragging={Boolean(activeItem)}
          //toggleGroupExpand={toggleGroupExpand}
        />
      ) : item.hoaiId && item.type == 'hoaiPhase' ? (
        <PositionsHOAIPhase
          props={props}
          key={'drag-' + item.keyId + item.id + '-' + 'Row'}
          collapseModules={collapseModules}
          index={index}
          items={items}
          item={item}
          columns={dynamicColumns}
          imageType={imageType}
          imageApiFilter={imageApiFilter}
          descriptionVars={descriptionVars}
          showVars={showVars}
          openAmount={openAmount}
          onFocus={(ref, key) =>
            onFocus({
              index: index,
              key,
              ref: ref,
            })
          }
          onChange={(newItems) => {
            setItems(calculateNewPositions(newItems, false, true));
          }}
          onCreate={handleNewPosition}
          onDelete={handleDelete}
          isDragging={Boolean(activeItem)}
        />
      ) : (
        <PositionsRow
          props={props}
          key={'drag-' + item.keyId + item.id + '-' + 'Row'}
          collapseModules={collapseModules}
          index={index}
          items={items}
          item={item}
          columns={dynamicColumns}
          imageType={imageType}
          imageApiFilter={imageApiFilter}
          descriptionVars={descriptionVars}
          showVars={showVars}
          openAmount={openAmount}
          onFocus={(ref, key) =>
            onFocus({
              index: index,
              key,
              ref: ref,
            })
          }
          onChange={(newItems) => {
            setItems(calculateNewPositions(newItems, false, true));
          }}
          onCreate={handleNewPosition}
          onDelete={handleDelete}
          isDragging={Boolean(activeItem)}
        />
      )}
    </Fragment>
  );
};

const PreviewElement = ({ colSpan, depth, posColWidth }) => {
  return (
    <tr>
      <td></td>
      {Array.from({ length: depth }).map((_, i) => (
        <td key={`depth-td-${i}`} />
      ))}
      <td colSpan={colSpan}>
        <Box className="flex w-full items-center mt-[-4px] mb-[-2px]">
          <Box
            className={`bg-blue-500 rounded-full ml-[2px]`}
            sx={{
              width: posColWidth - 4 + 'px',
              height: posColWidth - 4 + 'px',
            }}
          />
          <Box className="bg-blue-500 h-[4px] w-full" />
        </Box>
      </td>
    </tr>
  );
};

const DropDummy = ({ item, colSpan }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.sortId, disabled: true });

  return (
    <tr
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{
        transition,
        cursor: 'default',
      }}
    >
      <td colSpan={colSpan} className="h-[5px]" />
    </tr>
  );
};
